import React, { useCallback, useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Typography from "@tiptap/extension-typography";
import MarkdownIt from "markdown-it";
import TurndownService from "turndown";
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Minus,
  Image as ImageIcon,
  Link as LinkIcon,
  Undo2,
  Redo2,
  Pilcrow,
  FileUp,
  FileDown,
  Copy,
  FileCode,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { imagesApi } from "@/lib/apiClient";

// MD <-> HTML converters (module-level singletons).
const mdParser = new MarkdownIt({ html: true, linkify: true, breaks: false });
const turndown = new TurndownService({ headingStyle: "atx", codeBlockStyle: "fenced", bulletListMarker: "-" });

// Image node with align + width, persisted as data-* so they survive HTML
// export and the publish payload (the receiver styles via these attributes).
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: "center",
        renderHTML: (attrs) => ({ "data-align": attrs.align }),
        parseHTML: (el) => el.getAttribute("data-align") || "center",
      },
      width: {
        default: "full",
        renderHTML: (attrs) => ({ "data-width": attrs.width }),
        parseHTML: (el) => el.getAttribute("data-width") || "full",
      },
    };
  },
});

function ToolbarButton({ active, onClick, label, children, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-md text-foreground/70 transition-colors",
        "hover:bg-white/[0.05] hover:text-1",
        active && "bg-white/[0.07] text-1",
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span aria-hidden className="mx-1 h-5 w-px" style={{ backgroundColor: "var(--border-subtle)" }} />;
}

const fileToDataUri = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Upload an image file to the backend and return its served URL. Falls back to
// the data URI only if the upload fails, so an offline edit still shows something.
async function uploadImageFile(file) {
  const dataUri = await fileToDataUri(file);
  try {
    const res = await imagesApi.upload({ data_url: dataUri });
    return res?.url || dataUri;
  } catch {
    return dataUri;
  }
}

export function RichEditor({ value, onChange, onWordCount }) {
  const fileInputRef = useRef(null);
  const [mdOpen, setMdOpen] = useState(false);
  const [mdText, setMdText] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: { HTMLAttributes: { class: "rich-code-block" } },
      }),
      CustomImage.configure({
        HTMLAttributes: { class: "rich-image" },
        allowBase64: true,
        inline: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "rich-link" },
      }),
      Typography,
      Placeholder.configure({
        placeholder: "Start writing your blog post here… use / for blocks, drag images in, or paste anything.",
        emptyEditorClass: "is-editor-empty",
      }),
    ],
    content: value || "",
    autofocus: false,
    editorProps: {
      attributes: {
        class: "rich-editor-content focus:outline-none",
        "data-testid": "blog-rich-editor",
      },
      handleDrop: (view, event) => {
        const dt = event.dataTransfer;
        if (!dt || !dt.files || dt.files.length === 0) return false;
        const imgs = Array.from(dt.files).filter((f) => f.type.startsWith("image/"));
        if (imgs.length === 0) return false;
        event.preventDefault();
        const pending = toast.loading(`Uploading ${imgs.length} image${imgs.length > 1 ? "s" : ""}…`);
        Promise.all(imgs.map(uploadImageFile)).then((urls) => {
          urls.forEach((src) => editor?.chain().focus().setImage({ src, alt: "Image" }).run());
          toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} added`, { id: pending });
        }).catch(() => toast.error("Image upload failed", { id: pending }));
        return true;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        const imgs = Array.from(items).filter((it) => it.type.startsWith("image/"));
        if (imgs.length === 0) return false;
        event.preventDefault();
        Promise.all(imgs.map((it) => uploadImageFile(it.getAsFile()))).then((urls) => {
          urls.forEach((src) => editor?.chain().focus().setImage({ src, alt: "Image" }).run());
          toast.success("Image pasted");
        }).catch(() => toast.error("Image upload failed"));
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange?.(html);
      const text = ed.getText();
      onWordCount?.(text.split(/\s+/).filter(Boolean).length);
    },
  });

  // Sync external value updates (e.g. AI-generated draft replacing content)
  useEffect(() => {
    if (!editor) return;
    if (value && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
      onWordCount?.(editor.getText().split(/\s+/).filter(Boolean).length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const onPickImage = useCallback(() => fileInputRef.current?.click(), []);

  const onImageInputChange = useCallback(
    async (e) => {
      const files = Array.from(e.target.files || []);
      e.target.value = "";
      if (files.length === 0) return;
      const pending = toast.loading(`Uploading ${files.length} image${files.length > 1 ? "s" : ""}…`);
      try {
        const urls = await Promise.all(files.map(uploadImageFile));
        urls.forEach((src) => editor?.chain().focus().setImage({ src, alt: "Image" }).run());
        toast.success(`${files.length} image${files.length > 1 ? "s" : ""} added`, { id: pending });
      } catch {
        toast.error("Image upload failed", { id: pending });
      }
    },
    [editor],
  );

  const setImageAttr = useCallback(
    (attrs) => editor?.chain().focus().updateAttributes("image", attrs).run(),
    [editor],
  );

  const promptForLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const importMarkdown = useCallback(() => {
    if (!editor) return;
    const html = mdParser.render(mdText || "");
    // Append to existing content rather than wiping the draft.
    editor.chain().focus("end").insertContent(html).run();
    onChange?.(editor.getHTML());
    onWordCount?.(editor.getText().split(/\s+/).filter(Boolean).length);
    setMdText("");
    setMdOpen(false);
    toast.success("Markdown imported");
  }, [editor, mdText, onChange, onWordCount]);

  const copyAs = useCallback(async (kind) => {
    if (!editor) return;
    const html = editor.getHTML();
    const text = kind === "markdown" ? turndown.turndown(html) : html;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(kind === "markdown" ? "Copied as Markdown" : "Copied as HTML");
    } catch {
      toast.error("Could not copy — clipboard blocked");
    }
  }, [editor]);

  const downloadMarkdown = useCallback(() => {
    if (!editor) return;
    const md = turndown.turndown(editor.getHTML());
    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "post.md";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded post.md");
  }, [editor]);

  if (!editor) {
    return (
      <div className="rounded-xl border p-6 text-sm text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
        Loading editor…
      </div>
    );
  }

  const isActive = (name, attrs) => editor.isActive(name, attrs);
  const imgAttrs = editor.getAttributes("image");

  return (
    <div className="relative" data-testid="blog-rich-editor-shell">
      {/* Sticky toolbar */}
      <div
        className="sticky top-[56px] z-30 -mx-px flex flex-wrap items-center gap-0.5 rounded-t-xl border-b px-2 py-1.5 backdrop-blur supports-[backdrop-filter]:bg-[color:var(--surface-1)]/85"
        style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}
        data-testid="blog-editor-toolbar"
      >
        <ToolbarButton testId="rt-undo" label="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo2 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-redo" label="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo2 className="h-4 w-4" /></ToolbarButton>
        <Divider />
        <ToolbarButton testId="rt-p" label="Paragraph" active={isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()}><Pilcrow className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-h1" label="Heading 1" active={isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-h2" label="Heading 2" active={isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-h3" label="Heading 3" active={isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></ToolbarButton>
        <Divider />
        <ToolbarButton testId="rt-bold" label="Bold" active={isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-italic" label="Italic" active={isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-strike" label="Strikethrough" active={isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-code" label="Inline code" active={isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()}><Code className="h-4 w-4" /></ToolbarButton>
        <Divider />
        <ToolbarButton testId="rt-ul" label="Bulleted list" active={isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-ol" label="Numbered list" active={isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-quote" label="Block quote" active={isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-hr" label="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus className="h-4 w-4" /></ToolbarButton>
        <Divider />
        <ToolbarButton testId="rt-link" label="Link" active={isActive("link")} onClick={promptForLink}><LinkIcon className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-image" label="Insert image" onClick={onPickImage}><ImageIcon className="h-4 w-4" /></ToolbarButton>
        <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onImageInputChange} data-testid="blog-image-input" />
        <Divider />
        <ToolbarButton testId="rt-md-import" label="Import Markdown" onClick={() => setMdOpen(true)}><FileUp className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-md-copy" label="Copy as Markdown" onClick={() => copyAs("markdown")}><Copy className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-html-copy" label="Copy as HTML" onClick={() => copyAs("html")}><FileCode className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton testId="rt-md-download" label="Download .md" onClick={downloadMarkdown}><FileDown className="h-4 w-4" /></ToolbarButton>
      </div>

      {/* Image controls — only when an image node is selected */}
      {isActive("image") && (
        <div
          className="sticky top-[92px] z-20 -mx-px flex flex-wrap items-center gap-0.5 border-b px-2 py-1.5 text-[11px]"
          style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}
          data-testid="blog-image-controls"
        >
          <span className="px-1.5 text-muted-foreground">Image</span>
          <ToolbarButton testId="img-align-left" label="Align left" active={imgAttrs.align === "left"} onClick={() => setImageAttr({ align: "left" })}><AlignLeft className="h-4 w-4" /></ToolbarButton>
          <ToolbarButton testId="img-align-center" label="Align center" active={imgAttrs.align === "center"} onClick={() => setImageAttr({ align: "center" })}><AlignCenter className="h-4 w-4" /></ToolbarButton>
          <ToolbarButton testId="img-align-right" label="Align right" active={imgAttrs.align === "right"} onClick={() => setImageAttr({ align: "right" })}><AlignRight className="h-4 w-4" /></ToolbarButton>
          <Divider />
          {[["small", "S"], ["medium", "M"], ["large", "L"], ["full", "Full"]].map(([w, lbl]) => (
            <button
              key={w}
              type="button"
              data-testid={`img-width-${w}`}
              onClick={() => setImageAttr({ width: w })}
              title={`Width: ${w}`}
              className={cn(
                "flex h-8 min-w-[2rem] items-center justify-center rounded-md px-1.5 text-foreground/70 transition-colors hover:bg-white/[0.05] hover:text-1",
                imgAttrs.width === w && "bg-white/[0.07] text-1",
              )}
            >
              {lbl}
            </button>
          ))}
        </div>
      )}

      {/* Markdown import modal */}
      {mdOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="md-import-modal" onClick={() => setMdOpen(false)}>
          <div className="w-full max-w-2xl rounded-xl border bg-card p-5 shadow-xl" style={{ borderColor: "var(--border-subtle)" }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-1">Import Markdown</h3>
            <p className="mt-1 text-xs text-muted-foreground">Paste Markdown — it converts to formatted content and is inserted at the end of your post.</p>
            <textarea
              autoFocus
              value={mdText}
              onChange={(e) => setMdText(e.target.value)}
              rows={12}
              placeholder={"# My heading\n\nParagraph with **bold** and a [link](https://example.com).\n\n- bullet\n- bullet"}
              className="mt-3 w-full resize-y rounded-md border bg-[color:var(--surface-2)] px-3 py-2 font-mono text-[12.5px] text-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--accent-cyan)]/50"
              style={{ borderColor: "var(--border-subtle)" }}
              data-testid="md-import-textarea"
            />
            <div className="mt-3 flex items-center justify-end gap-2">
              <button type="button" onClick={() => { setMdOpen(false); setMdText(""); }} className="rounded-md px-3 py-1.5 text-[12.5px] text-muted-foreground hover:text-1" data-testid="md-import-cancel">Cancel</button>
              <button type="button" onClick={importMarkdown} disabled={!mdText.trim()} className="rounded-md px-3 py-1.5 text-[12.5px] font-medium text-white disabled:opacity-50" style={{ backgroundColor: "var(--accent-blue)" }} data-testid="md-import-confirm">Import</button>
            </div>
          </div>
        </div>
      )}

      <EditorContent editor={editor} />
    </div>
  );
}
